<script setup lang="ts">

import { computed, defineProps, defineEmits } from 'vue'
import { useMetadataStore } from '@/stores/metadataStore.ts'
import { getFilteringStringItem, getFilterItemKeyFromString } from '@/stores/filtering.ts'
import { useDisplay } from 'vuetify'

const props = defineProps<{
  filterPropertyKeys: string[],
  filterMap: Map<string, any[]>,
}>()

const emit = defineEmits(['filterChange'])

const mStore = useMetadataStore();
const nameMap = {
    'species_group': 'Species Group',
    'species_scientific': 'Species',
    'marker_types': 'Marker',
    'canton': 'Canton',
  }

const mobileLayout = computed(() => {
  const display = useDisplay();
  return display.smAndDown.value;
})


const exclusiveFiltering = computed(
  {
    get() {
      return mStore.exclusiveFiltering;
    },
    set(value: boolean) {
      mStore.changeExclusiveFiltering(value);
    },
  }
);

/*
const onlySensitiveData = computed({
  get() {
    return mStore.showSensitiveData;
  },
  set(value: boolean) {
    mStore.showSensitiveData = value;

    clearAllFilters();
  },
});
*/

const onlySwissData = computed({
  get() {
    return mStore.showSwissDataOnly === 'yes';
  },
  set(value: boolean) {
    mStore.showSwissDataOnly = value ? 'yes' : 'no';

    // Adjust propKey to be null when the toggle is off
    const filterItem = {
      filterKey: 'swiss_data',
      propKey: value ? ['yes'] : null, // Use null instead of ['no']
    };

    mStore.changeFilter(filterItem);
  },
})

const onlyPublishedData = computed({
  get() {
    return mStore.showDataSetPublishedOnly === 'yes';
  },
  set(value: boolean) {
    mStore.showDataSetPublishedOnly = value ? 'yes' : 'no';

    // Adjust propKey to be null when the toggle is off
    const filterItem = {
      filterKey: 'data_set_published',
      propKey: value ? ['yes'] : null, // Use null instead of ['no']
    };

    mStore.changeFilter(filterItem);
  },
})

const showGbifData = computed({
  get() {
    return mStore.showGbifData;
  },
  set(value: boolean) {
    mStore.showGbifData = value;
  }
})

const getDisplayName = (propertyKey: string) => {
  return nameMap[propertyKey as keyof typeof nameMap];
}

const filteredPropertyKeys = computed(() => {
  return mStore.filterPropertyKeys.filter(key => key !== 'swiss_data' && key !== 'data_set_published');
})

const selectedMetadata = computed(() => {
  return mStore.selectedMetadata || null;
})

const selectedMetadataValues = computed(() => {
  return selectedMetadata.value ? Object.values(selectedMetadata.value) : [];
})

const filteredMetadataSize = computed(() => {
  return mStore.filteredFeatures.features.length;
})

const addSelection = (filterKey: string, newValue: string | readonly string[] | null) => {
  let propKey: string[] | null = null;
  if (newValue) {
    propKey = getFilterItemKeyFromString(newValue, false);
  }

  emit('filterChange', { filterKey, propKey });
};

const clearAllFilters = () => {
  const keys = Object.keys(nameMap);
  keys.forEach((key: string) => {
    addSelection(key, null);
  })
};

const getFilterItems = (propertyKey: string) => {
  const filterItems = props.filterMap?.get(propertyKey);
  if (!filterItems) return [];

  const items = filterItems.map(item => getFilteringStringItem(item, false));
  return items.sort((a, b) => a.localeCompare(b));
}
</script>

<template>
  <v-container fluid >
    <v-row dense
           justify="space-between">
      <!--          <v-col class="px-4">-->
      <!--            <v-badge color="secondary" :content="filteredMetadataSize" />-->
      <!--          </v-col>-->

      <v-col cols="12" md="2">
        <v-switch label="Exclusive Filtering"
                  class="custom-font-style"
                  density="compact"
                  hide-details
                  color="primary"
                  v-model="exclusiveFiltering"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-switch label="Published Data"
                  class="custom-font-style"
                  density="compact"
                  hide-details
                  color="primary"
                  v-model="onlyPublishedData"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-switch label="Swiss Data Only"
                  class="custom-font-style"
                  density="compact"
                  hide-details
                  color="primary"
                  v-model="onlySwissData"
        />
      </v-col>

<!--
      <v-col cols="12" md="2">
        <v-switch label="Show Sensitive Data"
                  class="custom-font-style"
                  density="compact"
                  hide-details
                  color="primary"
                  v-model="onlySensitiveData"
        />
      </v-col>
-->

      <v-col v-if="!mobileLayout"
             cols="12" md="2" >
        <v-btn elevated :href="'https://gendib.wsl.ch/en/user-manual/'" target="_blank" class="user-manual-link full-size">
          User Manual
        </v-btn>
      </v-col>

    </v-row>
    
    <v-row dense
           class="mt-4 mt-md-0"
           justify="space-between">

      <v-col v-for="(propertyKey, index) in filteredPropertyKeys"
             :key="index"
             cols="12"
             md="3"
             style="overflow: hidden; white-space: nowrap; text-overflow: clip">
        <v-autocomplete :items="getFilterItems(propertyKey)"
                        :label="getDisplayName(propertyKey)"
                        @update:modelValue="addSelection(propertyKey, $event)"
                        clearable
                        :multiple="!exclusiveFiltering"
                        hide-details
                        single-line
                        class="custom-autocomplete">

          <!-- Custom selection slot -->
          <template v-slot:selection="{ item, index }">
            <!-- Only render the first selected item -->
            <span v-if="index === 0" class="custom-selection" :key="item.value">
                {{ item.value}}
                </span>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-row v-if="mobileLayout" class="mt-4">
      <v-col cols="12" md="3" class="btn-container">
        <v-btn elevated :href="'https://gendib.wsl.ch/en/user-manual/'" target="_blank" class="user-manual-link full-size">
          User Manual
        </v-btn>
      </v-col>
    </v-row>
    
  </v-container>

</template>

<style scoped>

.custom-font-style {
  font-family: 'Arial', sans-serif;
  font-weight: bold;
  color: #333;
}

.user-manual-link {
  text-transform: none;
  font-family: 'Arial', sans-serif;
  font-weight: bold;
  color: #858585;
}

.btn-container {
  height: 70%;
  width: 50%;
  justify-content: center;
}

.full-size {
  height: 80%;
  width: 80%;
  justify-content: center;
}
</style>
